import React, { useState, useEffect } from 'react';
import { meetingAPI } from '../services/api';
import { companyAdminAPI, buyerAdminAPI } from '../services/adminAPI';
import { useAuth } from '../context/AuthContext';

const MyMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchMeetings();
  }, [user]);

  const fetchMeetings = async () => {
    try {
      let response;
      if (user?.buyer) {
        response = await buyerAdminAPI.getBuyerMeetings();
        setMeetings(response.data.meetings);
      } else if (user?.is_admin && !user?.is_super_admin) {
        response = await companyAdminAPI.getCompanyMeetings();
        setMeetings(response.data.meetings);
      } else {
        response = await meetingAPI.getUserMeetings();
        setMeetings(response.data.meetings);
      }
    } catch (error) {
      setError('Error loading meetings');
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (meetingId, status) => {
    try {
      if (user?.is_admin && !user?.is_super_admin) {
        await companyAdminAPI.updateMeetingStatus(meetingId, status);
      }
      fetchMeetings();
    } catch (error) {
      setError(error.response?.data?.message || 'Error updating status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#EDB404';
      case 'approved':
        return '#28a745';
      case 'declined':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'approved':
      case 'accepted':
        return 'Approved';
      case 'declined':
        return 'Declined';
      case 'cancelled':
        return 'Cancelled';
      case 'rescheduled':
        return 'Rescheduled';
      default:
        return status;
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) {
      return;
    }
    try {
      if (user?.buyer) {
        await buyerAdminAPI.cancelMeeting(meetingId);
      } else if (user?.is_admin && !user?.is_super_admin) {
        await companyAdminAPI.cancelMeeting(meetingId);
      } else {
        await meetingAPI.cancelMeeting(meetingId);
      }
      fetchMeetings();
    } catch (error) {
      setError(error.response?.data?.message || 'Error cancelling meeting');
    }
  };

  const handleExportToExcel = async () => {
    try {
      let response;
      if (user?.buyer) {
        response = await buyerAdminAPI.exportMeetingsToExcel();
      } else if (user?.is_admin && !user?.is_super_admin) {
        response = await companyAdminAPI.exportMeetingsToExcel();
      } else {
        return; // Normal users don't have export
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my_meetings_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Error exporting meetings');
      console.error('Error exporting meetings:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: 'clamp(1rem, 3vw, 1.2rem)',
        color: '#666',
        backgroundColor: '#f8f9fa'
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: 'clamp(1rem, 3vw, 1.2rem)',
        color: '#dc3545',
        backgroundColor: '#f8f9fa'
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: 'clamp(10px, 3vw, 20px)',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <div style={{
        backgroundColor: 'white',
        padding: 'clamp(20px, 5vw, 30px)',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: 'clamp(20px, 4vw, 30px)',
        borderLeft: '5px solid #EDB404'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
              fontWeight: 'bold',
              color: '#333',
              margin: '0 0 clamp(8px, 2vw, 10px) 0',
              textAlign: 'left'
            }}>
              {user?.buyer ? 'My Meetings' : user?.is_admin && !user?.is_super_admin ? 'Incoming Meeting Requests' : 'My Meetings'}
            </h1>
            <p style={{
              fontSize: 'clamp(1rem, 3vw, 1.1rem)',
              color: '#666',
              textAlign: 'left',
              margin: '0',
              padding: '0'
            }}>
              {user?.buyer 
                ? 'View and manage your meeting requests' 
                : user?.is_admin && !user?.is_super_admin 
                ? 'Manage incoming meeting requests from participants' 
                : 'View and manage your meeting requests'}
            </p>
          </div>
          {(user?.is_admin && !user?.is_super_admin || user?.buyer) && meetings.length > 0 && (
            <button
              onClick={handleExportToExcel}
              style={{
                padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 4vw, 20px)',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#218838';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#28a745';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              📊 Export to Excel
            </button>
          )}
        </div>
      </div>

      {/* Meetings Grid */}
      {meetings.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(40px, 10vw, 60px) clamp(20px, 5vw, 30px)',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: 'clamp(3rem, 8vw, 4rem)',
            color: '#EDB404',
            marginBottom: 'clamp(15px, 4vw, 20px)'
          }}>
            📅
          </div>
          <h3 style={{
            fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
            color: '#333',
            marginBottom: 'clamp(8px, 2vw, 10px)'
          }}>
            {user?.buyer 
              ? 'You have no meeting requests yet' 
              : user?.is_admin && !user?.is_super_admin 
              ? 'No incoming meeting requests yet' 
              : 'You have no meeting requests yet'}
          </h3>
          <p style={{
            fontSize: 'clamp(1rem, 3vw, 1.1rem)',
            color: '#666',
            margin: '0'
          }}>
            {user?.buyer 
              ? 'Your meeting requests will appear here once companies request meetings with you' 
              : user?.is_admin && !user?.is_super_admin 
              ? 'Meeting requests from participants will appear here' 
              : 'Your meeting requests will appear here once you book them'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: 'clamp(15px, 3vw, 20px)',
          gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(300px, 40vw, 400px), 1fr))'
        }}>
          {meetings.map((meeting) => (
            <div
              key={meeting.id}
              style={{
                backgroundColor: 'white',
                padding: 'clamp(20px, 4vw, 25px)',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderLeft: `5px solid ${getStatusColor(meeting.status)}`,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
            >
              {/* Meeting Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 'clamp(15px, 3vw, 20px)'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                    fontWeight: 'bold',
                    color: '#333',
                    margin: '0 0 clamp(6px, 2vw, 8px) 0',
                    lineHeight: '1.3'
                  }}>
                    {user?.buyer 
                      ? `${meeting.company?.name || 'Unknown Company'} - ${meeting.meeting_slot?.start_time?.substring(0, 5) || ''}`
                      : user?.is_admin && !user?.is_super_admin 
                      ? `${meeting.requester_name || meeting.user?.name || meeting.buyer?.name || 'Unknown'} ${meeting.requester_surname || meeting.buyer?.surname || ''} - ${meeting.meeting_slot?.start_time?.substring(0, 5) || ''} meeting request`
                      : `${meeting.company?.name} - ${meeting.meeting_slot?.start_time?.substring(0, 5) || ''}`}
                  </h3>
                  
                  {user?.is_admin && !user?.is_super_admin && !user?.buyer && (
                    <div style={{
                      fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                      color: '#666',
                      marginTop: 'clamp(4px, 1vw, 6px)'
                    }}>
                      <div><strong>Email:</strong> {meeting.requester_email || meeting.user?.email || meeting.buyer?.email || 'N/A'}</div>
                      <div style={{ marginTop: '4px' }}><strong>Phone:</strong> {meeting.user?.phone || meeting.buyer?.phone || 'N/A'}</div>
                    </div>
                  )}
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(8px, 2vw, 10px)',
                    marginBottom: 'clamp(12px, 3vw, 15px)',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                      fontWeight: '600',
                      color: '#666'
                    }}>
                      Status:
                    </span>
                    <span
                      style={{
                        padding: 'clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px)',
                        borderRadius: '20px',
                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                        fontWeight: 'bold',
                        backgroundColor: getStatusColor(meeting.status) + '20',
                        color: getStatusColor(meeting.status),
                        border: `1px solid ${getStatusColor(meeting.status)}40`
                      }}
                    >
                      {getStatusText(meeting.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Meeting Details */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: 'clamp(12px, 3vw, 15px)',
                borderRadius: '8px',
                marginBottom: 'clamp(15px, 3vw, 20px)'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(120px, 25vw, 150px), 1fr))',
                  gap: 'clamp(8px, 2vw, 10px)'
                }}>
                  {meeting.meeting_slot && (
                    <>
                      <div>
                        <strong style={{ color: '#333', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)' }}>Date:</strong>
                        <div style={{ color: '#666', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)' }}>
                          {new Date(meeting.meeting_slot.meeting_date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                      <div>
                        <strong style={{ color: '#333', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)' }}>Time:</strong>
                        <div style={{ color: '#666', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)' }}>
                          {meeting.meeting_slot.start_time} - {meeting.meeting_slot.end_time}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: 'clamp(8px, 2vw, 10px)',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {user?.is_admin && !user?.is_super_admin && !user?.buyer && meeting.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleUpdateStatus(meeting.id, 'approved')}
                      style={{
                        flex: '1 1 auto',
                        minWidth: '120px',
                        padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 4vw, 20px)',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#218838';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#28a745';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      ✓ Accept
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(meeting.id, 'declined')}
                      style={{
                        flex: '1 1 auto',
                        minWidth: '120px',
                        padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 4vw, 20px)',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#c82333';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#dc3545';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      ✗ Decline
                    </button>
                  </>
                )}
                
                {/* Cancel Button - for both admin and user, if meeting is pending or approved */}
                {(meeting.status === 'pending' || meeting.status === 'approved' || meeting.status === 'rescheduled') && (
                  <button
                    onClick={() => handleCancelMeeting(meeting.id)}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '120px',
                      padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 4vw, 20px)',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f57c00';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#ff9800';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    🚫 Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyMeetings;
